import { ConversationState, Role } from '../types';
import { readable } from 'svelte/store';
export function hasAnyRole(session, ...roles) {
    return roles.some(role => session.roles.includes(role));
}
export function getConversationState(conversation) {
    if (conversation.archiveDate) {
        return ConversationState.ARCHIVED;
    }
    if (conversation.endDate) {
        return ConversationState.ENDED;
    }
    if (conversation.startDate) {
        return ConversationState.STARTED;
    }
    return ConversationState.CREATED;
}
export function isDetailDisplayable(conversation, session) {
    if (session.roles.includes(Role.INTERVISOR)) {
        return true;
    }
    if (session.roles.includes(Role.OPERATOR)) {
        return !conversation.operator || conversation.operator.id === session.id;
    }
    if (session.roles.includes(Role.CLIENT)) {
        return conversation.client.id === session.id;
    }
    return false;
}
export function isStartable(conversation, session) {
    return isPending(conversation) && session.roles.includes(Role.OPERATOR);
}
export function isArchivable(conversation, session) {
    return getConversationState(conversation) === ConversationState.ENDED && session.roles.includes(Role.OPERATOR);
}
export function isStarted(conversation) {
    return getConversationState(conversation) === ConversationState.STARTED;
}
export function isPending(conversation) {
    return getConversationState(conversation) === ConversationState.CREATED;
}
export function isPaused(conversation) {
    return !!conversation.pauseDate;
}
export function isArchived(conversation) {
    return !!conversation.archiveDate;
}
export function wasEnded(conversation) {
    return !!conversation.endDate;
}
export function wasStarted(conversation) {
    return !!conversation.startDate;
}
export function isOfSession(conversation, session) {
    var _a;
    return ((_a = conversation.operator) === null || _a === void 0 ? void 0 : _a.id) === session.id || conversation.client.id === session.id;
}
export function getAssignedActiveConversations(session, conversations) {
    if (session == null) {
        return [];
    }
    return conversations
        .filter(conv => isOfSession(conv, session))
        .filter(conv => wasStarted(conv))
        .filter(conv => !wasEnded(conv));
}
export function isDefinedMessageOverrideable(definedMessage, contextId) {
    return definedMessage.contextId !== contextId;
}
export function parseJwtPayload(token) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
}
export function toUrlParameters(obj, skipNulls = true) {
    return Object.entries(obj)
        .filter(([, val]) => skipNulls ? !!val : true)
        .map(([key, val]) => `${key}=${encodeURIComponent(val)}`)
        .join('&');
}
export function deepCompare(obj1, obj2) {
    return JSON.stringify(obj1) === JSON.stringify(obj2);
}
export function getSetAItemsMissingInSetB(setA, setB) {
    let setAItemsMissingInSetB = new Set(setB);
    for (let setAItem of setA) {
        setAItemsMissingInSetB.delete(setAItem);
    }
    return setAItemsMissingInSetB;
}
export function setsDifference(setA, setB) {
    let diff = new Set(setA);
    for (let elem of setB) {
        if (diff.has(elem)) {
            diff.delete(elem);
        }
        else {
            diff.add(elem);
        }
    }
    return diff;
}
export function getElapsedTime(dateFrom, dateTo) {
    const secondsDiff = (dateTo.getTime() - dateFrom.getTime()) / 1000;
    return toHHMMSS(secondsDiff);
}
export function toHHMMSS(numberOfSeconds) {
    let hours = Math.floor(numberOfSeconds / 3600);
    let minutes = Math.floor((numberOfSeconds - (hours * 3600)) / 60);
    let seconds = Math.floor(numberOfSeconds - (hours * 3600) - (minutes * 60));
    return `${hours < 10 ? '0' : ''}${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
}
export function replaceUrlsWithLink(message) {
    if (!message) {
        return '';
    }
    const urlRegex = /(((https?:\/\/)|(www\.))[^\s]+)/g;
    return message.replace(urlRegex, function (url) {
        let hyperlink = url;
        if (!hyperlink.match('^https?:\/\/')) {
            hyperlink = 'http://' + hyperlink;
        }
        return `<a href="${hyperlink}" target="_blank">${url}</a>`;
    });
}
export function escapeHtml(unsafe) {
    return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
}
export function assert(expression, failMessage = 'Assertion failed') {
    if (!expression) {
        throw new Error(failMessage);
    }
}
export function assertValued(item, failMessage = 'Given parameter must be defined') {
    assert(item !== undefined, failMessage);
    assert(item !== null, failMessage);
    return item;
}
export function nullIfUndefined(item) {
    if (item === undefined || item === null) {
        return null;
    }
    return item;
}
export function createAutoUpdatingStore(getValueFunction, intervalMillis) {
    return readable(getValueFunction(), function start(set) {
        const interval = setInterval(() => set(getValueFunction()), intervalMillis);
        return () => clearInterval(interval);
    });
}
export function getMaxDate(date1, date2) {
    if (date1 > date2) {
        return date1;
    }
    return date2;
}
export function cache(fn) {
    let fired = false;
    let res;
    return () => {
        if (!fired) {
            res = fn();
        }
        return res;
    };
}
export const identity = (x) => x;
